import type { MaskStylesObj } from "@reactour/mask"
import type { PopoverStylesObj } from "@reactour/popover"
import type { StylesObj } from "@reactour/tour"
import type { CSSProperties } from "react"
import type { ExtendedPosition } from "../types/tour.types"
import { calculatePopoverMargin } from "../utils/positionUtils"

// Combined type for all tour styles (TourProvider accepts this union)
type CombinedStylesObj = StylesObj & PopoverStylesObj & MaskStylesObj

// Mask style functions must return `CSSProperties & { rx?: number }`, while
// React 19's CSSProperties types the SVG `rx` property as `string | number`,
// so spreading a plain CSSProperties `base` does not type-check.
type MaskStyleBase = CSSProperties & { rx?: number }

interface TourStylesConfig {
  position?: ExtendedPosition
  stepPadding?: number
}

/**
 * Create tour provider styles based on current step configuration.
 * Extracted from OnboardingContext to improve maintainability.
 */
export const createTourStyles = (
  config?: TourStylesConfig,
): CombinedStylesObj => {
  const { position, stepPadding = 0 } = config || {}
  const margin = calculatePopoverMargin(position, stepPadding)

  return {
    popover: (base: CSSProperties) => ({
      ...base,
      backgroundColor: "hsl(var(--card))",
      borderRadius: "0.5rem",
      padding: "1.5rem",
      border: "2px solid rgba(255, 244, 64, 0.5)",
      boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1)",
      maxWidth: "400px",
      margin,
    }),
    maskWrapper: (base: MaskStyleBase) => ({
      ...base,
      color: "rgba(0, 0, 0, 0.85)",
    }),
    maskArea: (base: MaskStyleBase) => ({
      ...base,
      rx: 8,
    }),
    highlightedArea: (base: MaskStyleBase) => ({
      ...base,
      stroke: "#FFF440",
      strokeWidth: 6,
      rx: 8,
    }),
    badge: (base: CSSProperties) => ({
      ...base,
      backgroundColor: "#FFF440",
      color: "#000",
      fontWeight: 600,
    }),
    controls: (base: CSSProperties) => ({
      ...base,
      marginTop: "1.25rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "0.75rem",
    }),
    navigation: (base: CSSProperties) => ({
      ...base,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: "0.75rem",
    }),
    dot: (base: CSSProperties) => ({
      ...base,
      display: "none",
    }),
    close: (base: CSSProperties) => ({
      ...base,
      display: "none",
    }),
  }
}

/**
 * Get dynamic styles for the current tour step.
 * This is used within TourProvider to update styles based on current step.
 */
export const getTourStylesForStep = (
  position?: ExtendedPosition,
  stepPadding?: number,
): CombinedStylesObj => {
  return createTourStyles({ position, stepPadding })
}
